import L from 'leaflet';

function createBpIconHtml({ iconName, iconSize, color, background, iconOffset, topRightBadge, shape, bottomRightBadge }) {
	return baseIconHtml(`
	<use class="icon-outline" transform="${createTranslate(7.8, 6.8, iconOffset)}" height="${iconSize}rem" fill="${color}" xlink:href="#${iconName}"/>
		`, color, background, topRightBadge, shape, bottomRightBadge);
}

function createTimpexIconHtml({ iconName, iconSize, color, fillColor, background, iconOffset, topRightBadge, shape = 'circle', bottomRightBadge }) {
	return baseIconHtml(`
	<use class="icon-fill" transform="${createTranslate(-0.3, 3, iconOffset)}" height="${iconSize}rem" fill="${fillColor}" xlink:href="#${iconName}-fill"/>
	<use class="icon-outline" transform="${createTranslate(-0.3, 3, iconOffset)}" height="${iconSize}rem" fill="${color}" xlink:href="#${iconName}-outline"/>
		`, color, background, topRightBadge, shape, bottomRightBadge);
}

function createTranslate(defaultOffsetX, defaultOffsetY, iconOffset) {
	let offsetX = iconOffset && iconOffset[0] ? iconOffset[0] : 0;
	let offsetY = iconOffset && iconOffset[1] ? iconOffset[1] : 0;
	return `translate(${defaultOffsetX + offsetX}, ${defaultOffsetY + offsetY})`;
}

function createCircle(background) {
	return `
		<circle fill="url('#myGradient')" cx="1rem" cy="1rem" r="1rem"></circle>}
		<circle transform="translate(1.3, 0)" fill="${background}" cx=".9rem" cy=".9rem" r=".9rem" class="marker-background"></circle>
	`.trim();
}

function createRect(background) {
	return `
		<rect width="1.8rem" height="1.8rem" rx="3" ry="3" fill="#999" filter="url(#blurFilter)" transform="translate(0,2)"></rect>}
		<rect width="1.8rem" height="1.8rem" rx="3" ry="3" fill="${background}" class="marker-background"></rect>
	`.trim();
}

function createMarker(background) {
	return `<g transform="scale(.7) translate(6.8,3)">
	<path transform="translate(0,3)" fill="#000" opacity=".4" filter="url(#blurFilter)" d="M 38,15.964253 C 38,31.20887 16,47 16,47 16,47 -6,30.667288 -6,15.964253 -6,3.8337312 3.8497353,-5.9999998 16,-6 28.150264,-6 38,3.8337311 38,15.964253 Z"/>
	<path class="marker-background" fill="${background}" d="M 38,15.964253 C 38,31.20887 16,47 16,47 16,47 -6,30.667288 -6,15.964253 -6,3.8337312 3.8497353,-5.9999998 16,-6 28.150264,-6 38,3.8337311 38,15.964253 Z"/>
	</g>`.trim();
}

function baseIconHtml(content, color, background, topRightBadge, shape, bottomRightBadge) {
	return `<svg height="2rem" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg" style="transform-origin: -110% 100%; position: relative; overflow: visible" xmlns:xlink="http://www.w3.org/1999/xlink">
	<defs>
	<radialGradient id="myGradient">
		<stop offset="40%" stop-color="#999" />
		<stop offset="100%" stop-color="transparent" />
	</radialGradient>
	<filter id="blurFilter">
		<feGaussianBlur in="SourceGraphic" stdDeviation="2" />
	</filter>
	</defs>
	<g id="Page-1" fill="none" fill-rule="evenodd">
	${shape === 'circle' ? createCircle(background) : ''}
	${shape === 'square' ? createRect(background) : ''}
	${shape === 'marker' ? createMarker(background) : ''}
	${content}
	</g>
	${topRightBadge ? createBadge(topRightBadge, color, background, '27.5,3') : ''}
	${bottomRightBadge ? createBadge(bottomRightBadge, '#425A70', '#FFFFFF', '27.5,28') : ''}
</svg>`;
}

function createBadge(count, color, background, translate) {
	if (!Number.isInteger(count)) return '';
	let y = -7;
	let numberOfDigits = count.toString().length;
	let width = calculateBadgeWidth(numberOfDigits);
	let x = calculateBadgeOffsetX(numberOfDigits);
	return `<g transform="translate(${translate})">
		<rect x="${x}" y="${y}" rx=".45rem" ry=".45rem" width="${width}rem" height=".9rem" fill="${color}" stroke="rgba(0,0,0,.2)"/>
		<text fill="${background}" 
			font-size=".6rem"
			font-family="'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', 'Droid Sans Mono', 'Source Code Pro', monospace" 
			text-anchor="middle"
			dy="0.37em">
				${count}
			</text>
		</g>`;
}

function calculateBadgeWidth(numberOfDigits) {
	return 0.9 + (0.33 * (numberOfDigits - 1));
}

function calculateBadgeOffsetX(numberOfDigits) {
	return -7.2 - (2.65 * (numberOfDigits - 1));
}

export default {
	props: {
		iconName: {
			type: String,
			default: 'map-marker'
		},
		iconType: {
			type: String,
			default: 'bp'
		},
		iconSize: {
			type: Number,
			default: 1.4
		},
		iconOffset: {
			type: Array,
			default: () => ([ 0,0 ])
		},
		color: {
			type: String,
			default: '#1070ca'
		},
		fillColor: {
			type: String,
			default: '#ddebf7'
		},
		background: {
			type: String,
			default: 'white'
		},
		size: {
			type: Number,
			default: 10
		},
		anchor: {
			type: Array,
			default: () => ([ 15,14 ])
		},
		shape: {
			type: String,
			default: 'circle',
			validator: (value) => [ 'circle', 'marker', 'square' ].indexOf(value) > -1
		},
	},
	methods: {
		createIcon({ iconName = this.iconName, iconType = this.iconType, iconOffset = this.iconOffset, iconSize = this.iconSize, color = this.color, fillColor = this.fillColor, background = this.background, size = this.size, shape = this.shape, topRightBadge, cluster = null, anchor = this.anchor, ...options }) {
			let html = '';
			if (cluster && cluster.terminals.length ) {
				html = createBpIconHtml({ iconName: 'office', iconSize, color, fillColor: (fillColor || '#ddebf7'), background, iconOffset: [ -1.2, 0 ], topRightBadge: cluster.pickup_count - cluster.terminals.length, bottomRightBadge: cluster.delivery_count, shape:'square' });
			} else if ( cluster ) {
				html = createTimpexIconHtml({ iconName: 'orders', iconSize, color, fillColor: (fillColor || '#ddebf7'), background, iconOffset: [ -1.2, 0 ], topRightBadge: cluster.pickup_count, bottomRightBadge: cluster.delivery_count, shape:'square' });
			} else if (iconType === 'bp') {
				html = createBpIconHtml({ iconName, iconSize, color, background, iconOffset, topRightBadge, shape });
			} else {
				html = createTimpexIconHtml({ iconName, iconSize, color, fillColor, background, iconOffset, topRightBadge, shape });
			}

			return L.divIcon({
				className: 'map-icon',
				html,
				iconSize: size,
				iconAnchor: anchor,
				...options
			});
		}
	}
};
